/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { CreditCard, Pencil, Plus, Trash2, Users } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../services/apiService";
import { Badge } from "../../../components/ops/Badge";
import { CollapsibleRow } from "../../../components/ops/CollapsibleRow";
import { useConfirm } from "../../../components/ops/ConfirmProvider";
import { DataToolbar } from "../../../components/ops/DataToolbar";
import { EmptyState } from "../../../components/ops/EmptyState";
import { Modal } from "../../../components/ops/Modal";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Pagination } from "../../../components/ops/Pagination";
import { useToast } from "../../../components/ops/ToastProvider";
import { usePagedList } from "../../../components/ops/usePagedList";

const empty = { name: "", slug: "", ageGroup: "all", description: "", price: "", dropInPrice: "", billingInterval: "monthly", sessionsPerMonth: 4, features: "", active: true };
const centsToDollars = (cents) => (cents ? (cents / 100).toFixed(2) : "");
const toList = (value) => value.split(",").map((item) => item.trim()).filter(Boolean);
const toText = (value) => (value || []).join(", ");
const statusTone = { active: "success", pending: "neutral", past_due: "warning", paused: "neutral", cancelled: "danger" };
const enrollmentStatuses = ["pending", "active", "past_due", "paused", "cancelled"];
const intervalLabels = { monthly: "mo", quarterly: "qtr", annual: "yr", one_time: "one-time" };

export function AdminMembershipsPage() {
  const { accessToken } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [tab, setTab] = useState("plans");
  const [plans, setPlans] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [planSearch, setPlanSearch] = useState("");
  const [planStatusFilter, setPlanStatusFilter] = useState("all");
  const [enrollmentSearch, setEnrollmentSearch] = useState("");
  const [enrollmentStatusFilter, setEnrollmentStatusFilter] = useState("all");

  const filteredPlans = useMemo(() => {
    return plans.filter((plan) => {
      if (planStatusFilter !== "all" && (planStatusFilter === "active") !== Boolean(plan.active)) return false;
      return true;
    });
  }, [plans, planStatusFilter]);

  const planPages = usePagedList(filteredPlans, { search: planSearch, searchFields: ["name", "slug"], pageSize: 6 });

  const filteredEnrollments = useMemo(() => {
    return enrollments.filter((enrollment) => {
      if (enrollmentStatusFilter !== "all" && enrollment.status !== enrollmentStatusFilter) return false;
      return true;
    });
  }, [enrollments, enrollmentStatusFilter]);

  const enrollmentPages = usePagedList(filteredEnrollments, {
    search: enrollmentSearch,
    searchFields: ["player.firstName", "player.lastName", "parent.firstName", "parent.lastName", "parent.email", "plan.name"],
    pageSize: 8,
  });

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/admin/plans"), api.get("/memberships/enrollments")])
      .then(([plansResponse, enrollmentsResponse]) => {
        setPlans(plansResponse.data.plans || []);
        setEnrollments(enrollmentsResponse.data.enrollments || []);
      })
      .catch(() => toast.error("Unable to load membership data"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [accessToken]);

  const openCreate = () => {
    setEditingId(null);
    setForm(empty);
    setModalOpen(true);
  };

  const openEdit = (plan) => {
    setEditingId(plan._id);
    setForm({
      name: plan.name,
      slug: plan.slug,
      ageGroup: plan.ageGroup,
      description: plan.description || "",
      price: centsToDollars(plan.priceCents),
      dropInPrice: centsToDollars(plan.dropInPriceCents),
      billingInterval: plan.billingInterval,
      sessionsPerMonth: plan.sessionsPerMonth,
      features: toText(plan.features),
      active: plan.active,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(empty);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const { price, dropInPrice, ...rest } = form;
    const payload = {
      ...rest,
      priceCents: Math.round(Number(price) * 100),
      dropInPriceCents: Math.round(Number(dropInPrice || 0) * 100),
      sessionsPerMonth: Number(form.sessionsPerMonth),
      features: toList(form.features),
    };

    try {
      if (editingId) {
        const response = await api.patch(`/admin/plans/${editingId}`, payload);
        setPlans(plans.map((p) => (p._id === editingId ? response.data.plan : p)));
        toast.success("Membership plan updated successfully!");
      } else {
        const response = await api.post("/admin/plans", payload);
        setPlans([response.data.plan, ...plans]);
        toast.success("Membership plan created successfully!");
      }
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to save plan");
    } finally {
      setSaving(false);
    }
  };

  const togglePlan = async (plan) => {
    try {
      const response = await api.patch(`/admin/plans/${plan._id}`, { active: !plan.active });
      setPlans(plans.map((p) => (p._id === response.data.plan._id ? response.data.plan : p)));
      toast.success(response.data.plan.active ? "Plan activated." : "Plan deactivated.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update plan");
    }
  };

  const removePlan = async (plan) => {
    const ok = await confirm({
      title: "Delete this plan?",
      message: `"${plan.name}" will be permanently removed. This can't be undone.`,
      confirmLabel: "Delete plan",
      tone: "danger",
    });
    if (!ok) return;

    try {
      await api.delete(`/admin/plans/${plan._id}`);
      setPlans(plans.filter((p) => p._id !== plan._id));
      toast.success("Plan deleted.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to delete plan");
    }
  };

  const changeEnrollmentStatus = async (enrollment, status) => {
    try {
      const response = await api.patch(`/memberships/enrollments/${enrollment._id}/status`, { status });
      setEnrollments(enrollments.map((e) => (e._id === response.data.enrollment._id ? response.data.enrollment : e)));
      toast.success("Enrollment updated.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update enrollment");
    }
  };

  const removeEnrollment = async (enrollment) => {
    const ok = await confirm({
      title: "Delete this enrollment?",
      message: `The enrollment for "${enrollment.player?.firstName || "this player"}" will be permanently removed. This can't be undone.`,
      confirmLabel: "Delete enrollment",
      tone: "danger",
    });
    if (!ok) return;

    try {
      await api.delete(`/memberships/enrollments/${enrollment._id}`);
      setEnrollments(enrollments.filter((e) => e._id !== enrollment._id));
      toast.success("Enrollment deleted.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to delete enrollment");
    }
  };

  return (
    <div>
      <PageHeader title="Memberships" subtitle="Manage pricing plans and family enrollments" />

      <div className="ops-chip-row">
        <button className={`ops-chip ${tab === "plans" ? "is-active" : ""}`} onClick={() => setTab("plans")}>
          <CreditCard size={13} /> All Plans
        </button>
        <button className={`ops-chip ${tab === "enrollments" ? "is-active" : ""}`} onClick={() => setTab("enrollments")}>
          <Users size={13} /> All Enrollments
        </button>
      </div>

      {tab === "plans" && (
      <div className="ops-card">
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
          <button type="button" className="ops-button ops-button--primary" onClick={openCreate}>
            <Plus size={16} /> New Plan
          </button>
        </div>
        <DataToolbar
          search={planSearch}
          onSearchChange={setPlanSearch}
          searchPlaceholder="Search plans by name..."
          filters={[
            {
              label: "Status",
              value: planStatusFilter,
              onChange: setPlanStatusFilter,
              options: [{ value: "all", label: "All statuses" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }],
            },
          ]}
          resultCount={planPages.totalItems}
          itemLabel="plans"
        />
        {loading ? (
          <p className="ops-loading">Loading plans...</p>
        ) : planPages.totalItems === 0 ? (
          <EmptyState icon={CreditCard} title="No plans found" description={plans.length === 0 ? "Create your first membership plan to get started." : "Try adjusting your search or filters."} />
        ) : (
          <>
            <div className="ops-cards-grid">
              {planPages.pageItems.map((plan) => (
                <div key={plan._id} className="ops-entity-card">
                  <div className="ops-entity-card-header">
                    <div className="ops-entity-card-icon"><CreditCard size={20} /></div>
                    <div className="ops-entity-card-heading">
                      <h3 className="ops-entity-card-title">{plan.name}</h3>
                      <p className="ops-entity-card-subtitle">{plan.ageGroup === "all" ? "All ages" : plan.ageGroup}</p>
                    </div>
                    <Badge tone={plan.active ? "success" : "neutral"}>{plan.active ? "Active" : "Inactive"}</Badge>
                  </div>
                  <div className="ops-entity-card-stats">
                    <div className="ops-entity-card-stat">
                      <span className="ops-entity-card-stat-label">Price</span>
                      <span className="ops-entity-card-stat-value">${(plan.priceCents / 100).toFixed(2)} / {intervalLabels[plan.billingInterval] || plan.billingInterval}</span>
                    </div>
                    <div className="ops-entity-card-stat">
                      <span className="ops-entity-card-stat-label">Sessions/mo</span>
                      <span className="ops-entity-card-stat-value">{plan.sessionsPerMonth}</span>
                    </div>
                    <div className="ops-entity-card-stat">
                      <span className="ops-entity-card-stat-label">Drop-in</span>
                      <span className="ops-entity-card-stat-value">{plan.dropInPriceCents > 0 ? `$${(plan.dropInPriceCents / 100).toFixed(2)}` : "—"}</span>
                    </div>
                    <div className="ops-entity-card-stat">
                      <span className="ops-entity-card-stat-label">Age group</span>
                      <span className="ops-entity-card-stat-value">{plan.ageGroup === "all" ? "All ages" : plan.ageGroup}</span>
                    </div>
                  </div>
                  {plan.description && (
                    <div className="ops-entity-card-body">
                      <p>{plan.description}</p>
                    </div>
                  )}
                  {plan.features?.length > 0 && (
                    <div className="ops-entity-card-tags">
                      {plan.features.map((feature) => <span key={feature} className="ops-entity-card-tag">{feature}</span>)}
                    </div>
                  )}
                  <div className="ops-entity-card-actions">
                    <button className="ops-button ops-button--outline ops-button--small" onClick={() => openEdit(plan)}>
                      <Pencil size={13} /> Edit
                    </button>
                    <button className="ops-button ops-button--outline ops-button--small" onClick={() => togglePlan(plan)}>
                      {plan.active ? "Deactivate" : "Activate"}
                    </button>
                    <button className="ops-button ops-button--danger ops-button--small" onClick={() => removePlan(plan)} aria-label="Delete plan">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Pagination page={planPages.page} totalPages={planPages.totalPages} onPageChange={planPages.setPage} />
          </>
        )}
      </div>
      )}

      {tab === "enrollments" && (
      <div className="ops-card">
        <DataToolbar
          search={enrollmentSearch}
          onSearchChange={setEnrollmentSearch}
          searchPlaceholder="Search by player or parent..."
          filters={[
            {
              label: "Status",
              value: enrollmentStatusFilter,
              onChange: setEnrollmentStatusFilter,
              options: [{ value: "all", label: "All statuses" }, ...enrollmentStatuses.map((status) => ({ value: status, label: status.replace("_", " ") }))],
            },
          ]}
          resultCount={enrollmentPages.totalItems}
          itemLabel="enrollments"
        />
        {loading ? (
          <p className="ops-loading">Loading enrollments...</p>
        ) : enrollmentPages.totalItems === 0 ? (
          <EmptyState icon={Users} title="No enrollments found" description={enrollments.length === 0 ? "Enrollments appear here once families complete checkout." : "Try adjusting your search or filters."} />
        ) : (
          <>
            <ul className="ops-list">
              {enrollmentPages.pageItems.map((enrollment) => (
                <CollapsibleRow
                  key={enrollment._id}
                  avatar={<CreditCard size={16} />}
                  title={`${enrollment.player?.firstName || ""} ${enrollment.player?.lastName || ""} — ${enrollment.plan?.name || "Plan"}`}
                  meta={`${enrollment.parent?.firstName || ""} ${enrollment.parent?.lastName || ""} (${enrollment.parent?.email || "—"})`}
                  trailing={
                    <>
                      <Badge tone={statusTone[enrollment.status] || "neutral"}>{enrollment.status.replace("_", " ")}</Badge>
                      <select
                        className="ops-select"
                        style={{ width: "auto", padding: "6px 10px" }}
                        value={enrollment.status}
                        onChange={(e) => changeEnrollmentStatus(enrollment, e.target.value)}
                      >
                        {enrollmentStatuses.map((status) => <option key={status} value={status}>{status.replace("_", " ")}</option>)}
                      </select>
                      <button type="button" className="ops-button ops-button--danger ops-button--small" onClick={() => removeEnrollment(enrollment)} aria-label="Delete enrollment">
                        <Trash2 size={13} />
                      </button>
                    </>
                  }
                >
                  <p><strong>Plan price:</strong> ${((enrollment.plan?.priceCents || 0) / 100).toFixed(2)} / {enrollment.plan?.billingInterval || "month"}</p>
                  <p><strong>Enrolled:</strong> {new Date(enrollment.createdAt).toLocaleDateString()}</p>
                </CollapsibleRow>
              ))}
            </ul>
            <Pagination page={enrollmentPages.page} totalPages={enrollmentPages.totalPages} onPageChange={enrollmentPages.setPage} />
          </>
        )}
      </div>
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingId ? "Edit Membership Plan" : "New Membership Plan"}
        size="lg"
        footer={
          <>
            <button type="submit" form="membership-plan-form" className="ops-button ops-button--primary" disabled={saving}>{saving ? "Saving..." : editingId ? "Save Changes" : "Create Plan"}</button>
            <button type="button" className="ops-button ops-button--secondary" onClick={closeModal}>Cancel</button>
          </>
        }
      >
        <form id="membership-plan-form" onSubmit={submit} className="ops-form">
          <div className="ops-form-section">
            <p className="ops-form-section-title">Basic information</p>
            <div className="ops-form-grid">
              <div>
                <label className="ops-form-label">Plan name</label>
                <input required className="ops-input" placeholder="Elite Membership" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Slug (URL)</label>
                <input required className="ops-input" placeholder="elite-membership" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              </div>
            </div>
            <div className="ops-form-group">
              <label className="ops-form-label">Description</label>
              <textarea className="ops-textarea" placeholder="What's included..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="ops-form-group">
              <label className="ops-form-label">Features <span className="ops-form-label-optional">(optional)</span></label>
              <input className="ops-input" placeholder="Unlimited sessions, Priority booking, ..." value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} />
              <span className="ops-form-hint">Comma-separated</span>
            </div>
          </div>

          <div className="ops-form-section">
            <p className="ops-form-section-title">Plan settings</p>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Age group</label>
                <select className="ops-select" value={form.ageGroup} onChange={(e) => setForm({ ...form, ageGroup: e.target.value })}>
                  <option value="all">All ages</option>
                  <option value="1-3">Ages 1-3</option>
                  <option value="4-5">Ages 4-5</option>
                  <option value="6-8">Ages 6-8</option>
                  <option value="9-11">Ages 9-11</option>
                  <option value="12-19">Ages 12-19</option>
                  <option value="adult">Adults</option>
                </select>
              </div>
              <div>
                <label className="ops-form-label">Billing interval</label>
                <select className="ops-select" value={form.billingInterval} onChange={(e) => setForm({ ...form, billingInterval: e.target.value })}>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                  <option value="one_time">One-time</option>
                </select>
              </div>
              <div>
                <label className="ops-form-label">Sessions / month</label>
                <input type="number" min="0" className="ops-input" value={form.sessionsPerMonth} onChange={(e) => setForm({ ...form, sessionsPerMonth: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="ops-form-section">
            <p className="ops-form-section-title">Pricing</p>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Member price ($)</label>
                <input required type="number" min="0" step="0.01" className="ops-input" placeholder="0.00" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                <span className="ops-form-hint">What a member pays per billing interval</span>
              </div>
              <div>
                <label className="ops-form-label">Drop-in price ($) <span className="ops-form-label-optional">(optional)</span></label>
                <input type="number" min="0" step="0.01" className="ops-input" placeholder="0.00" value={form.dropInPrice} onChange={(e) => setForm({ ...form, dropInPrice: e.target.value })} />
                <span className="ops-form-hint">Non-member, per-session price</span>
              </div>
            </div>
          </div>

        </form>
      </Modal>
    </div>
  );
}
